import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { FiCamera, FiUser, FiPhone, FiCalendar, FiMail, FiShield, FiCheckCircle, FiEdit2 } from 'react-icons/fi';
import Modal from "../components/modal";
import { IoCloudUploadOutline } from "react-icons/io5";
import { uploadProfileImageById, updateUserById } from "../api/reusableApis";
import ToastNotification from "../components/Notification";
import { setLoggedInUser } from '../features/auth/authSlice';
import dayjs from 'dayjs';
import BasicDatePicker from '../components/BasicDatePicker';

const Profile = () => {
    const currentLoggedInUser = useSelector((state) => state.auth.currentUser);
    const [openModal, setOpenModal] = useState(false);
    const [mode, setMode] = useState("Image");
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [profileImageUrl, setProfileImageUrl] = useState('');
    const [toast, setToast] = useState({ message: "", type: "success" });

    // Form States
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState(dayjs());
    const [formattedDob, setFormattedDob] = useState('');
    const [errors, setErrors] = useState({});

    const dispatch = useDispatch();

    useEffect(() => {
        const url = currentLoggedInUser?.['users']?.['user_img_url'];
        setProfileImageUrl(url || 'https://ui-avatars.com/api/?background=72398E&color=fff&name=' + currentLoggedInUser['first_name']);
    }, [currentLoggedInUser]);

    const handleEventDate = (value) => {
        setDateOfBirth(value);
        setFormattedDob(value ? dayjs(value).format('YYYY-MM-DD') : '');
    }

    const closeModal = () => {
        setOpenModal(false);
        setPreviewUrl('');
        setSelectedFile(null);
        setErrors({});
    };

    const openEditModal = (mode) => {
        setFirstName(currentLoggedInUser['first_name']);
        setLastName(currentLoggedInUser['last_name']);
        setPhoneNumber(currentLoggedInUser['users']['phone']);
        setDateOfBirth(dayjs(currentLoggedInUser['users']['dateOfBirth']));
        setFormattedDob(currentLoggedInUser['users']['dateOfBirth']);
        setMode(mode);
        setOpenModal(true);
    }

    const handleFileChange = (e) => {
        const files = e.target.files;
        if (files && files[0]) {
            setSelectedFile(files[0]);
            setPreviewUrl(URL.createObjectURL(files[0]));
        }
    };

    async function uploadProfileImage() {
        if (!selectedFile) return;
        let formdata = new FormData();
        formdata.set('profile_picture', selectedFile);
        const user_id = currentLoggedInUser['users']['ROWID'];
        const response = await uploadProfileImageById(user_id, formdata);

        if (response?.data) {
            const updatedUser = {
                ...currentLoggedInUser,
                users: { ...currentLoggedInUser.users, user_img_url: response.data.profile_picture[0]['user_img_url'] }
            };
            dispatch(setLoggedInUser(updatedUser));
            closeModal();
            setToast({ message: "Profile picture updated!", type: "success" });
        }
    }

    async function updateUserDetails() {
        const valErrors = validateForm();
        if (Object.keys(valErrors).length === 0) {
            let requestObj = { firstName, lastName, phone: phoneNumber, dateOfBirth: formattedDob };
            const user_id = currentLoggedInUser['users']['ROWID'];
            const response = await updateUserById(requestObj, user_id);

            if (response?.data?.success) {
                let userDetails = response.data.user_management;
                userDetails['users'] = response.data.user.data[0];
                dispatch(setLoggedInUser(userDetails));
                closeModal();
                setToast({ message: "Profile updated successfully", type: "success" });
            }
        } else {
            setErrors(valErrors);
        }
    }

    const validateForm = () => {
        let errs = {};
        if (!firstName.trim()) errs.firstName = 'Required';
        if (!lastName.trim()) errs.lastName = 'Required';
        if (!phoneNumber.trim()) errs.phoneNumber = 'Required';
        return errs;
    };

    return (
        <div className="flex-1 px-4 sm:px-8 py-8 pt-24 lg:pt-8 bg-[#F8FAFC] min-h-screen">
            {/* Header Section */}
            <div className="mb-8">
                <h1 className="text-3xl font-black text-slate-800 tracking-tight">Account Settings</h1>
                <p className="text-slate-500 font-medium">Manage your personal information and profile preferences.</p>
            </div>

            <div className="max-w-5xl space-y-6">
                {/* Profile Card */}
                <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="h-32 bg-gradient-to-r from-[#72398E] to-[#9d5dbd]" />
                    <div className="px-8 pb-8">
                        <div className="relative flex flex-col sm:flex-row sm:items-end -mt-12 gap-6">
                            <div className="relative group">
                                <img
                                    src={profileImageUrl}
                                    alt="Profile"
                                    className="w-32 h-32 rounded-3xl border-4 border-white shadow-xl object-cover bg-white"
                                />
                                <button
                                    onClick={() => { setMode('Image'); setOpenModal(true); }}
                                    className="absolute bottom-2 right-2 p-2 bg-white rounded-xl shadow-lg border border-slate-100 text-[#72398E] hover:scale-110 transition-transform"
                                >
                                    <FiCamera size={18} />
                                </button>
                            </div>
                            <div className="flex-1 mb-2">
                                <h2 className="text-2xl font-bold text-slate-800">{currentLoggedInUser['first_name']} {currentLoggedInUser['last_name']}</h2>
                                <div className="flex flex-wrap gap-3 mt-2">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-purple-50 text-[#72398E] border border-purple-100">
                                        <FiShield className="mr-1" /> {currentLoggedInUser['role_details']['role_name']}
                                    </span>
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-600 border border-emerald-100">
                                        <FiCheckCircle className="mr-1" /> {currentLoggedInUser['status']}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => openEditModal('edit')}
                                className="flex items-center gap-2 px-6 py-2.5 bg-[#72398E] text-white rounded-xl font-bold text-sm shadow-lg shadow-purple-200 hover:bg-[#5a2d71] transition-all"
                            >
                                <FiEdit2 size={16} /> Edit Profile
                            </button>
                        </div>
                    </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InfoCard title="Personal Details" icon={<FiUser className="text-[#72398E]" />}>
                        <DataRow label="Full Name" value={`${currentLoggedInUser['first_name']} ${currentLoggedInUser['last_name']}`} />
                        <DataRow label="Date of Birth" value={currentLoggedInUser['users']['dateOfBirth'] || 'Not Set'} />
                        <DataRow label="Phone" value={currentLoggedInUser['users']['phone'] || 'Not Set'} />
                    </InfoCard>

                    <InfoCard title="Account Details" icon={<FiShield className="text-[#72398E]" />}>
                        <DataRow label="Email Address" value={currentLoggedInUser['email_id']} icon={<FiMail size={14} />} />
                        <DataRow label="User ID" value={`#${currentLoggedInUser['users']['ROWID']}`} />
                        <DataRow label="Platform Status" value={currentLoggedInUser['status']} />
                    </InfoCard>
                </div>
            </div>

            {/* Modal Logic */}
            <Modal open={openModal} onClose={closeModal} title={mode === "Image" ? "Update Profile Picture" : "Edit Personal Details"} width="max-w-md">
                <div className="p-2">
                    {mode === 'Image' ? (
                        <div className="space-y-6">
                            <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-3xl p-8 bg-slate-50 hover:bg-purple-50 hover:border-[#72398E] transition-all group">
                                {previewUrl ? (
                                    <img src={previewUrl} className="w-40 h-40 rounded-2xl object-cover shadow-md mb-4" alt="preview" />
                                ) : (
                                    <IoCloudUploadOutline className="text-5xl text-slate-300 group-hover:text-[#72398E] mb-4" />
                                )}
                                <label className="cursor-pointer">
                                    <span className="bg-white px-4 py-2 rounded-lg border border-slate-200 text-sm font-bold shadow-sm hover:bg-slate-50">Select Image</span>
                                    <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                                </label>
                            </div>
                            <div className="flex gap-3">
                                <button onClick={closeModal} className="flex-1 py-3 text-sm font-bold text-slate-500">Cancel</button>
                                <button onClick={uploadProfileImage} className="flex-1 py-3 bg-[#72398E] text-white rounded-xl font-bold shadow-lg shadow-purple-100">Upload</button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <FormInput label="First Name" value={firstName} onChange={setFirstName} error={errors.firstName} />
                                <FormInput label="Last Name" value={lastName} onChange={setLastName} error={errors.lastName} />
                            </div>
                            <FormInput label="Phone Number" value={phoneNumber} onChange={setPhoneNumber} error={errors.phoneNumber} />
                            <div className="flex flex-col">
                                <label className="text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">Date of Birth</label>
                                <BasicDatePicker handleDatePicker={handleEventDate} valueData={dateOfBirth} />
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button onClick={closeModal} className="flex-1 py-3 text-sm font-bold text-slate-500">Cancel</button>
                                <button onClick={updateUserDetails} className="flex-1 py-3 bg-[#72398E] text-white rounded-xl font-bold shadow-lg shadow-purple-100">Save Changes</button>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>

            <ToastNotification message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />
        </div>
    );
};

// Helper Components for cleaner code
const InfoCard = ({ title, icon, children }) => (
    <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-50">
            <div className="p-2 bg-purple-50 rounded-lg">{icon}</div>
            <h3 className="font-bold text-slate-800">{title}</h3>
        </div>
        <div className="space-y-4">{children}</div>
    </div>
);

const DataRow = ({ label, value, icon }) => (
    <div className="flex flex-col">
        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{label}</span>
        <div className="flex items-center gap-2 mt-1">
            {icon && <span className="text-slate-400">{icon}</span>}
            <span className="text-sm font-semibold text-slate-700">{value}</span>
        </div>
    </div>
);

function FormInput({ label, value, onChange, error }) {
    return (
        <div className="flex flex-col">
            <label className="text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">{label}</label>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={`w-full border rounded-xl px-4 py-2.5 text-sm transition-all outline-none 
                ${error ? 'border-rose-400 focus:ring-rose-500/10' : 'border-slate-200 focus:border-[#72398E] focus:ring-4 focus:ring-purple-500/5'}`}
            />
            {error && <span className="text-[10px] text-rose-500 mt-1 font-bold italic">{error}</span>}
        </div>
    );
}

export default Profile;